// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Dashboard components
import LineAreaChart from "components/stylishChart";
import { useLazyDashboardDataQuery } from "services/main";
import { useRevenueGraphMutation } from "services/main";
import { useOrderGraphMutation } from "services/main";
import { useEffect, useState } from "react";
import { FormControl, MenuItem, Select } from "@mui/material";
import useTranslation from "hooks/Translation";

function Analytics() {
  const t = useTranslation();
  const [dashboard] = useLazyDashboardDataQuery();
  const [dashboardData, setDashboardData] = useState("");
  const [orderGraph] = useOrderGraphMutation();
  const [revenueGraph] = useRevenueGraphMutation();
  const [orderKeys, setOrderKeys] = useState([]);
  const [revenueKeys, setRevenueKeys] = useState([]);
  const [orderValues, setOrderValues] = useState([]);
  const [revenueValues, setRevenueValues] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState("3");
  const [selectedRevenue, setSelectedRevenue] = useState("3");

  function convertToInternationalCurrencySystem(labelValue) {
    const num = Math.abs(Number(labelValue));

    if (num >= 1.0e9) return (num / 1.0e9).toFixed(2) + " B"; // Billions
    if (num >= 1.0e6) return (num / 1.0e6).toFixed(2) + " M"; // Millions
    if (num >= 1.0e3) return (num / 1.0e3).toFixed(2) + " K"; // Thousands
    return num.toFixed(2); // Less than 1K
  }

  const getDashboard = async () => {
    try {
      const res = await dashboard({}).unwrap();
      if (res?.status === 200) {
        setDashboardData(res?.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getOrderGraph = async () => {
    try {
      let body = { interval: Number(selectedOrder) };
      const res = await orderGraph(body).unwrap();

      if (res?.status === 200 && res?.data) {
        setOrderKeys(Object.keys(res.data)); // Extracts months (keys)
        setOrderValues(Object.values(res.data)); // Extracts order values
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getRevenueGraph = async () => {
    try {
      let body = { interval: Number(selectedRevenue) };
      const res = await revenueGraph(body).unwrap();

      if (res?.status === 200 && res?.data) {
        setRevenueKeys(Object.keys(res.data));
        setRevenueValues(Object.values(res.data)); // Extracts revenue values
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getDashboard();
  }, []);

  useEffect(() => {
    getOrderGraph();
    getRevenueGraph();
  }, [selectedOrder, selectedRevenue]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="weekend"
                title={t.main.monthlySales}
                count={
                  dashboardData?.monthly_revenue !== undefined
                    ? `AED ${convertToInternationalCurrencySystem(
                        dashboardData?.monthly_revenue
                      )}`
                    : "-"
                }
                // percentage={{
                //   color: "success",
                //   amount: "+55%",
                //   label: "than lask week",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="leaderboard"
                title={t.main.commissionPaid}
                count={
                  dashboardData?.commission_paid !== undefined
                    ? `AED ${convertToInternationalCurrencySystem(
                        dashboardData?.commission_paid
                      )}`
                    : "-"
                }
                // percentage={{
                //   color: "success",
                //   amount: "+3%",
                //   label: "than last month",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="store"
                title={t.main.revenue}
                count={
                  dashboardData?.total_revenue !== undefined
                    ? `AED ${convertToInternationalCurrencySystem(
                        dashboardData?.total_revenue
                      )}`
                    : "-"
                }
                // percentage={{
                //   color: "success",
                //   amount: "+1%",
                //   label: "than yesterday",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="store"
                title={t.main.repeatCustomers}
                count="65%"
                // percentage={{
                //   color: "success",
                //   amount: "+1%",
                //   label: "than yesterday",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="store"
                title={t.main.averageFeedback}
                count="4.7/5"
                // percentage={{
                //   color: "success",
                //   amount: "+1%",
                //   label: "than yesterday",
                // }}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5} mb={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox sx={{ position: "relative" }}>
                <MDBox
                  sx={{
                    position: "absolute",
                    zIndex: 1,
                    right: 0,
                    margin: 1,
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      className="select_div"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectedOrder}
                      displayEmpty
                      onChange={(val) => setSelectedOrder(val?.target?.value)}
                      sx={{
                        width: 150,
                        border: "1px solid grey", // Add a custom border color
                        borderRadius: "6px", // Optional: Add border radius for styling
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Adjust border color for the outlined variant
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color on hover
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color when focused
                        },
                      }}
                    >
                      <MenuItem value={"1"}>{t.main.daily}</MenuItem>
                      <MenuItem value={"2"}>{t.main.weekly}</MenuItem>
                      <MenuItem value={"3"}>{t.main.monthly}</MenuItem>
                      <MenuItem value={"4"}>{t.main.yearly}</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <LineAreaChart
                  keys={orderKeys}
                  values={orderValues}
                  label={"Orders"}
                />
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3} sx={{ position: "relative" }}>
                <MDBox
                  sx={{
                    position: "absolute",
                    zIndex: 1,
                    right: 0,
                    margin: 1,
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      className="select_div"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectedRevenue}
                      displayEmpty
                      onChange={(val) => setSelectedRevenue(val?.target?.value)}
                      sx={{
                        width: 150,
                        border: "1px solid grey", // Add a custom border color
                        borderRadius: "6px", // Optional: Add border radius for styling
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Adjust border color for the outlined variant
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color on hover
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color when focused
                        },
                      }}
                    >
                      <MenuItem value={"1"}>{t.main.daily}</MenuItem>
                      <MenuItem value={"2"}>{t.main.weekly}</MenuItem>
                      <MenuItem value={"3"}>{t.main.monthly}</MenuItem>
                      <MenuItem value={"4"}>{t.main.yearly}</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <LineAreaChart
                  keys={revenueKeys}
                  values={revenueValues}
                  label={"Sales"}
                  backgroundColor="rgba(144, 238, 144, 0.2)"
                  borderColor="rgba(144, 238, 144, 1)"
                  pointBackgroundColor="rgba(144, 238, 144, 1)"
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
